
import {
    createTheme,
    ThemeProvider
} from '@mui/material/styles';
import NotificationContext
    from "../utils/NotificationContext";

const darkStyles = {
    backgroundColor: "rgb(30 41 59 / var(--tw-bg-opacity))",
    color: 'white',
};

const defaultTheme = {
	palette: {
		primary: {
			light: "#0fa3ff",
			main: "#0093EF",
			dark: "#006fb3",
		},
		secondary: {
			main: "#E1E1F5",
			light: "#E8E8FD",
			dark: "#D3D3E8",
			darkest: "#3A35411F"
		},
		success: {
			light: "#1e8d49",
			main: "#23a455",
			dark: "#27bd61",
			contrastText: "#ffffff",
		},
		error: {
			light: "#ff686d",
			main: "#FF4C51",
			dark: "#ec3a40",
			contrastText: "#ffffff",
		},
		jobs: {
			light: "#ff686d",
			main: "#FF4C51",
			dark: "#ec3a40",
			contrastText: "#ffffff",
		},
		contacts: {
			main: "#111",
			contrastText: "#ffffff",
		},
		neutral: {
			main: "#eee",
		},
		textPrimary: {
			main: `#001623`,
		},
		bodyBackground: {
			main: "#F4F5FA",
			muted: "#F9FAFC"
		},
		otherDivider: {
			main: "rgba(58, 53, 65, 0.12)",
		},
		customBackground: {
			main: "#ebf6fe",
		},
		dividerLight: {
			main: "rgba(255, 255, 255, 0.30)",
		},
		white: {
			main: "#fff",
		},
		black: {
			main: "#000",
		},
		crmTag: {
			main: "#d6efff",
			contrastText: "#444444",
		},
		crmIconLink: {
			main: "#008de5",
		},
		contactsNew: {
            main: '#d889ae',
            contrastText: '#fff'
        },
        forms: {
            main: '#a2d165',
            contrastText: '#fff'
        },
		pillTab: {
			main: '#e0f2fd'
		},
		payNow: {
			main: '#901AC6',
		},
		powerupActive: {
			on: "#4db600",
			off: "#89868d"
		},
		navigation: {
			main: '#7e7b82',
			active: '#0393ef',
			email: '#0393ef',
			message: '#0393ef',
			letter: '#0393ef',
			call: '#0393ef',
			notification: '#0393ef',
			background: '#f4f4f4',
			crm: '#51bae6',
			contacts: '#d889ae',
			sales: '#7988eb',
			forms: '#a2d165',
			jobs: '#f1665e',
			scheduler: '#ad97b5',
			resources: '#f39453',
			documents: '#f39453',
			accounts: '#8457a6',
			quotes: '#8457a6',
			invoices: '#8457a6',
			tasks: '#33a96a',
			workflows: '#75d8ad',
			assets: '#b14c82',
			reports: '#fbd266',
			admin: '#ba7bb0',
			partner: '#0093ef',
		},
	},
	typography: {
		fontFamily: "Outfit",
		fontSize: 15,
		body1: {
			fontFamily: "Outfit",
			fontSize: 14,
		},
		body2: {
			fontFamily: "Outfit",
			fontSize: 14,
		},
		subtitle1: {
			fontFamily: "Outfit",
			fontSize: 14,
		},
		subtitle2: {
			fontFamily: "Outfit",
			fontSize: 14,
			fontWeight: 500,
		},
		body2semibold: {
			fontFamily: "Outfit",
			fontSize: 14,
			fontWeight: 500,
		},
		h1: {
			fontFamily: "Outfit",
			fontSize: 24,
			fontWeight: 400,
		},
		h2: {
			fontFamily: "Outfit",
			fontSize: 22,
			fontWeight: 400,
		},
		h3: {
			fontFamily: "Outfit",
			fontSize: 20,
			fontWeight: 400,
		},
		h4: {
			fontFamily: "Outfit",
			fontSize: 18,
			fontWeight: 400,
		},
		h5: {
			fontFamily: "Outfit",
			fontSize: 16,
			fontWeight: 400,
		},
		h6: {
			fontFamily: "Outfit",
			fontSize: 14,
			fontWeight: 500,
		},
	},
	components: {
		// Name of the component ⚛️
		MuiChip: {
			styleOverrides: {
				root: ({ ownerState, theme }) => {
					if (ownerState.color === 'crmTag') {
						const deleteStyles = {
							'& .MuiChip-deleteIcon': {
								color: theme.palette.crmTag.contrastText,
							},
							'& .MuiChip-deleteIcon:hover': {
								color: '#111'
							}
						}
						return {
							backgroundColor: theme.palette.crmTag.main,
							color: theme.palette.crmTag.contrastText,
							...deleteStyles
						}
					}					
				},
			},
			defaultProps: {
				color: "crmTag",
			},
		},
		MuiButtonBase: {
			defaultProps: {
				// The props to apply
				disableRipple: true, // No more ripple, on the whole application 💣!
			},
		},
		MuiMenu: {
			defaultProps: {
				disableScrollLock: true,
				// disablePortal: true,
			},
		},
		MuiIconButton: {
			defaultProps: {
				color: "textPrimary",
			},
		},
		MuiButton: {
			styleOverrides: {
				root: ({ ownerState }) => {
					const commonStyles = {
						textTransform: "none",
						lineHeight: "1.6em",
						fontWeight: 400,
					};
					
					if (ownerState.size === "small") {
						return {
							...commonStyles,
							padding: "8.5px 16px",
						};
					}
					
					return {
						...commonStyles,
						padding: "11.5px 24px",
					};
				},
			},
			defaultProps: {
				disableElevation: true,
			},
			
		},
		MuiFormControlLabel: {
			styleOverrides: {
				root: {
					marginLeft: "-4px",
				},
			},
		},
		MuiRadio: {
			styleOverrides: {
				root: {
					padding: "4px",

					"& svg": {
						fontSize: "1.4em",
					},
				},
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: {
					padding: "4px",

					"& svg": {
						fontSize: "1.4em",
					},
				},
			},
		},
		// MuiOutlinedInput: {
		//     styleOverrides: {
		//         input: {
		//             padding: '10px'
		//         }
		//     }
		// },
		MuiInputBase: {
			styleOverrides: {
				input: {
					height: "1.1em",
					lineHeight: "1.1em",
				},
			},
		},
		MuiSelect: {
			styleOverrides: {
				select: {
					minHeight: "1.1em",
				},
			},
		},
		MuiPopover: {
			defaultProps: {
				// disablePortal: true, // Disables portals for Popover by default
			},
		},
		MuiInput: {
			// defaultProps: {
			//     disableUnderline: true
			// }
			styleOverrides: {
				// Name of the slot
				underline: {
					":after": {
						borderBottomWidth: "1px",
					},
					":hover:not(.Mui-disabled):before": {
						borderBottomWidth: "1px",
					},
				},
			},
		},
		MuiTablePagination: {
			styleOverrides: {
				displayedRows: {
					marginBottom: 0,
				},
				selectLabel: {
					marginBottom: 0,
				},
			},
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					fontSize: "1rem",
					backgroundColor: "white",
					boxShadow: "0 0 3px rgba(0,0,0,0.2)",
					color: "inherit",
					p: "0.5rem",
				},
			},
		},
		MuiTabs: {
			styleOverrides: {
				indicator: {
					height: 4,
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					fontSize: "14px",
					textTransform: "none",
					fontWeight: "400",
					fontFamily: "Outfit",
				},
			},
		},
		MuiAutocomplete: {
			styleOverrides: {
				root: {
					backgroundColor: "white",
				},
			},
		},
		// MuiAutocomplete: {
		//     variants: [
		//         {
		//             props: {variant: 'dark'},
		//         }
		//     ],
		//     styleOverrides: {
		//         root: ({
		//                    ownerState,
		//                    theme,
		//                    ...rest
		//                }) => {

		//             const styles = {
		//                 '& .MuiOutlinedInput-root': {
		//                     padding: '3px 6px',
		//                 },
		//                 '& .MuiInputLabel-root': {
		//                     ...(!ownerState.open && !ownerState.hasClearIcon && !ownerState.focused ? { transform: 'translate(14px, 12px) scale(1)' } : {})
		//                 }
		//             }

		//             if (ownerState.variant === 'dark') {
		//                 return {
		//                     ...darkStyles,
		//                     ...styles
		//                 };
		//             }

		//             return {
		//                 backgroundColor: "white",
		//                 ...styles
		//             }
		//         },
		//         listbox: ({
		//                       ownerState,
		//                       theme
		//                   }) => {
		//             return ownerState.variant === 'dark' && darkStyles;
		//         },
		//         loading: ({
		//                       ownerState,
		//                       theme
		//                   }) => {
		//             return ownerState.variant === 'dark' && darkStyles;
		//         },
		//         noOptions: ({
		//                         ownerState,
		//                         theme
		//                     }) => {
		//             return ownerState.variant === 'dark' && darkStyles;
		//         },
		//         clearIndicator: ({
		//                              ownerState,
		//                              theme
		//                          }) => {
		//             return ownerState.variant === 'dark' && ({
		//                 color: 'white',
		//             });
		//         },
		//         popupIndicator: ({
		//                              ownerState,
		//                              theme
		//                          }) => {
		//             return ownerState.variant === 'dark' && ({
		//                 color: 'white',
		//             });
		//         },
		//         inputRoot: ({
		//                         ownerState,
		//                         theme
		//                     }) => {
		//             return ownerState.variant === 'dark' && ({
		//                 color: 'white',
		//                 border: 'solid 1px rgb(100, 116, 139)',
		//             });
		//         }
		//     }
		// },
		MuiPaper: {
			defaultProps: {
				elevation: 3,
			},
			styleOverrides: {
				elevation1: {
					boxShadow:
						"0px 1px 3px 0px rgba(58, 53, 65, 0.12), 0px 1px 1px 0px rgba(58, 53, 65, 0.14), 0px 2px 1px -1px rgba(58, 53, 65, 0.20)",
				},
				elevation2: {
					boxShadow:
						"0px 1px 5px 0px rgba(58, 53, 65, 0.12), 0px 2px 2px 0px rgba(58, 53, 65, 0.14), 0px 3px 1px -2px rgba(58, 53, 65, 0.20)",
				},
				elevation3: {
					boxShadow:
						"0px 4px 8px -4px rgba(58, 53, 65, 0.16), 0px 6px 10px -4px rgba(58, 53, 65, 0.08), 0px 4px 6px -4px rgba(58, 53, 65, 0.10)",
				},
				elevation4: {
					boxShadow:
						"0px 6px 16px -4px rgba(58, 53, 65, 0.20), 0px 6px 16px -4px rgba(58, 53, 65, 0.08), 0px 6px 14px -8px rgba(58, 53, 65, 0.10)",
				},
				elevation5: {
					boxShadow:
						"0px 1px 14px 0px rgba(58, 53, 65, 0.12), 0px 5px 8px 0px rgba(58, 53, 65, 0.14), 0px 3px 5px -1px rgba(58, 53, 65, 0.20)",
				},
				elevation6: {
					boxShadow:
						"0px 6px 16px -4px rgba(58, 53, 65, 0.10), 0px 2px 12px -4px rgba(58, 53, 65, 0.08), 0px 2px 12px -4px rgba(58, 53, 65, 0.10)",
				},
				elevation7: {
					boxShadow:
						"0px 2px 16px 1px rgba(58, 53, 65, 0.12), 0px 7px 10px 1px rgba(58, 53, 65, 0.14), 0px 4px 5px -2px rgba(58, 53, 65, 0.20)",
				},
				elevation8: {
					boxShadow:
						"0px 3px 14px 2px rgba(58, 53, 65, 0.12), 0px 8px 10px 1px rgba(58, 53, 65, 0.14), 0px 5px 5px -3px rgba(58, 53, 65, 0.20)",
				},
			},
			variants: [
				{
					props: {
						variant: "flatGrey",
					},
					defaultProps: {
						elevation: 0,
					},
					style: ({ theme }) => {
						return {
							backgroundColor: `${theme.palette.bodyBackground.main}`,
						};
					},
				},
				{
					props: {
						variant: "flatWhite",
					},
					defaultProps: {
						elevation: 0,
					},
					style: ({ theme }) => {
						return {
							backgroundColor: `${theme.palette.common.white}`,
						};
					},
				},
			],
		},
	},
	transitions: {
		// So we have `transition: none;` everywhere
		// create: () => 'none',
	},
	hiddenLabel: true,
	zIndex: {
		snackbar: 999999999999999
	}
};

const MuiTheme = ({
                      theme = {},
                      children,
                  }) => {
    const muiTheme = createTheme({...defaultTheme, ...theme});

    return (
        <ThemeProvider theme={muiTheme}>
            <NotificationContext>
                {children}
            </NotificationContext>
        </ThemeProvider>
    )
}

export default MuiTheme;
export {MuiTheme}
